if (!customElements.get('sticky-atc-bar')) {
  customElements.define(
    'sticky-atc-bar',
    class StickyAtcBar extends HTMLElement {
      constructor() {
        super();
        document.body.classList.add('sticky-atc-bar-enabled');

        this.selectors = {
          variantIdSelect: '[name="id"]',
        };
      }

      get quantityInput() {
        return this.querySelector('quantity-input input');
      }
      get quantity() {
        return this.querySelector('quantity-input');
      }
      get sectionId() {
        return this.dataset.originalSection || this.dataset.section;
      }
      get productId() {
        return this.getAttribute('data-product-id');
      }
      get select() {
        return this.querySelector('select');
      }
      get productForm() {
        return this.querySelector('form[is="product-form"]');
      }

      connectedCallback() {
        this.productFormActions = document.querySelector(`.main-product-form[data-product-id="${this.productId}"]`);
        this.mainProductInfo = document.querySelector(`product-info[data-product-id="${this.productId}"]`);
        this.container = this.closest('.sticky-atc-bar');
        this.submitButton = this.querySelector('[type="submit"]');
        this.selectedVariantId = this.querySelector(this.selectors.variantIdSelect).value;

        this.variantData = this.getVariantData();
        this.initVariantPicker();

        this.scheduleButtonStyleSync = () => {
          cancelAnimationFrame(this.buttonStyleFrame);
          this.buttonStyleFrame = requestAnimationFrame(() => this.syncButtonStyles());
        };
        window.addEventListener('resize', this.scheduleButtonStyleSync);
        document.addEventListener('shopify:section:load', this.scheduleButtonStyleSync);
        this.buttonStyleObserver = new MutationObserver(this.scheduleButtonStyleSync);
        this.buttonStyleObserver.observe(this, { childList: true, subtree: true });
        this.scheduleButtonStyleSync();
        document.fonts?.ready.then(() => {
          if (this.isConnected) this.scheduleButtonStyleSync();
        });

        this.init();
        this.select.addEventListener('change', (e) => {
          if (this.isUpdating) return;
          this.isUpdating = true;

          this.updateQuantityInput();

          this.mainVariantSelects = this.mainProductInfo && this.mainProductInfo.querySelector('variant-selects');
          const selectedVariantId = this.querySelector(this.selectors.variantIdSelect).value;
          this.currentVariant = this.variantData.find((variant) => variant.id === Number(selectedVariantId));

          if (this.mainVariantSelects) {
            Array.from(this.mainVariantSelects.querySelectorAll('select, fieldset'), (element, index) => {
              const variantOptionVal = this.currentVariant.options[index];
              switch (element.tagName) {
                case 'SELECT':
                  element.value = variantOptionVal;
                  const options = element.querySelectorAll('option');
                  options.forEach((option) => option.removeAttribute('selected'));

                  element.value = variantOptionVal;
                  const selectedOption = element.querySelector(`option[value="${variantOptionVal}"]`);
                  if (selectedOption) {
                    selectedOption.setAttribute('selected', 'selected');
                  }
                  break;
                case 'FIELDSET':
                  Array.from(element.querySelectorAll('input')).forEach((radio) => {
                    if (radio.value === variantOptionVal) {
                      radio.checked = true;
                    }
                  });
                  break;
              }
            });
            setTimeout(() => {
              this.mainVariantSelects.dispatchEvent(new Event('change', { detail: { formStickty: true } }));
              this.isUpdating = false;
            }, 0);
          } else {
            this.isUpdating = false;
          }

          this.updatePrice();
          this.updateButton(true, '', false);
          if (!this.currentVariant) {
            this.updateButton(true, '', true);
          } else {
            this.updateButton(!this.currentVariant.available, FoxTheme.variantStrings.soldOut);
          }
        });

        const hasRequiredFields = this.validateMainProductRequiredFields();
        if (hasRequiredFields) {
          this.submitButton.addEventListener('click', (e) => {
            e.preventDefault();
            e.stopPropagation();
            this.productFormActions.scrollIntoView({ behavior: 'smooth', block: 'center' });
            setTimeout(() => {
              this.productFormActions.requestSubmit();
            }, 300);
          });
        }
      }

      disconnectedCallback() {
        this.variantPickerEvents?.abort();
        cancelAnimationFrame(this.buttonStyleFrame);
        window.removeEventListener('resize', this.scheduleButtonStyleSync);
        document.removeEventListener('shopify:section:load', this.scheduleButtonStyleSync);
        this.buttonStyleObserver?.disconnect();
      }

      syncButtonStyles() {
        this.syncVariantPicker?.();
        const mainForm = document.querySelector(`.main-product-form[data-product-id="${this.productId}"]`);
        const mainButton = mainForm?.querySelector('.product-form__submit');
        if (!mainButton) return;

        // Read the actual template styles, including merchant settings and mobile rules.
        // Keep sticky layout, availability, variant selection and submit behavior intact.
        const properties = [
          'border-radius', 'border-width', 'border-style', 'border-color',
          'background-color', 'color', 'font-family', 'font-size', 'font-weight',
          'font-style', 'letter-spacing', 'text-transform', 'line-height', 'min-height', 'height',
        ];
        const pairs = [
          [mainButton, this.querySelector('.product-form__submit')],
          [mainForm.querySelector('.shopify-payment-button__button--unbranded') || mainButton,
            this.querySelector('.shopify-payment-button__button--unbranded')],
        ];
        for (const [source, target] of pairs) {
          if (!target) continue;
          const styles = getComputedStyle(source);
          for (const property of properties) {
            target.style.setProperty(property, styles.getPropertyValue(property));
          }
          for (const layer of ['before', 'after']) {
            target.style.setProperty(`--sticky-button-${layer}`, getComputedStyle(source, `::${layer}`).backgroundColor);
          }
          target.classList.add('sticky-atc-bar__matched-button');
        }
        this.checkDevice();
      }

      initVariantPicker() {
        const select = this.select;
        const wrap = select?.closest('.select');
        if (!wrap || wrap.classList.contains('hidden')) return;
        this.variantPickerEvents?.abort();
        this.variantPickerEvents = new AbortController();
        const options = { signal: this.variantPickerEvents.signal };
        wrap.querySelectorAll('.opt-select__trigger, .opt-select__panel').forEach((node) => node.remove());
        const trigger = document.createElement('button');
        trigger.type = 'button';
        trigger.className = 'opt-select__trigger';
        trigger.setAttribute('aria-haspopup', 'listbox');
        trigger.setAttribute('aria-expanded', 'false');
        trigger.innerHTML = '<span class="opt-select__value"></span><svg class="opt-select__chevron" viewBox="0 0 12 8" fill="none" aria-hidden="true"><path d="M1 1.5 6 6.5l5-5" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/></svg>';
        const label = trigger.querySelector('span');
        const panel = document.createElement('ul');
        panel.className = 'opt-select__panel';
        panel.id = `StickyVariantOptions-${this.sectionId}`;
        panel.setAttribute('role', 'listbox');
        panel.setAttribute('aria-label', 'Choose variant');
        panel.hidden = true;
        trigger.setAttribute('aria-controls', panel.id);
        const items = Array.from(select.options, (option) => {
          const item = document.createElement('li');
          item.className = 'opt-select__option';
          item.setAttribute('role', 'option');
          item.setAttribute('aria-disabled', String(option.disabled));
          item.tabIndex = -1;
          item.dataset.value = option.value;
          item.textContent = option.textContent.trim();
          panel.appendChild(item);
          return item;
        });
        const close = (focus = false) => {
          panel.hidden = true;
          trigger.setAttribute('aria-expanded', 'false');
          if (focus) trigger.focus();
        };
        const open = () => {
          panel.hidden = false;
          trigger.setAttribute('aria-expanded', 'true');
          (items.find((item) => item.dataset.value === select.value) || items[0])?.focus();
        };
        const choose = (item) => {
          if (!item || item.getAttribute('aria-disabled') === 'true') return;
          close(true);
          if (select.value !== item.dataset.value) {
            select.value = item.dataset.value;
            select.dispatchEvent(new Event('change', { bubbles: true }));
          }
          this.syncVariantPicker();
        };
        this.syncVariantPicker = () => {
          const text = select.selectedOptions[0]?.textContent.trim() || '';
          if (label.textContent !== text) label.textContent = text;
          trigger.setAttribute('aria-label', `Choose variant: ${text}`);
          items.forEach((item) => item.setAttribute('aria-selected', String(item.dataset.value === select.value)));
        };
        trigger.addEventListener('click', () => panel.hidden ? open() : close(), options);
        panel.addEventListener('click', (event) => choose(event.target.closest('.opt-select__option')), options);
        select.addEventListener('change', this.syncVariantPicker, options);
        wrap.addEventListener('keydown', (event) => {
          if (event.key === 'Escape') { close(true); event.preventDefault(); }
          else if (event.key === 'Tab') close();
          else if (['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(event.key)) {
            event.preventDefault();
            if (panel.hidden) { open(); return; }
            const enabled = items.filter((item) => item.getAttribute('aria-disabled') !== 'true');
            let index = enabled.indexOf(document.activeElement);
            if (event.key === 'Home') index = 0;
            else if (event.key === 'End') index = enabled.length - 1;
            else index = Math.max(0, Math.min(enabled.length - 1, index + (event.key === 'ArrowDown' ? 1 : -1)));
            enabled[index]?.focus();
          } else if ((event.key === 'Enter' || event.key === ' ') && items.includes(event.target)) {
            event.preventDefault(); choose(event.target);
          }
        }, options);
        document.addEventListener('click', (event) => { if (!wrap.contains(event.target)) close(); }, options);
        wrap.append(trigger, panel);
        select.tabIndex = -1;
        select.setAttribute('aria-hidden', 'true');
        wrap.classList.add('opt-select', 'is-enhanced');
        this.syncVariantPicker();
      }

      validateMainProductRequiredFields() {
        const mainForm = this.mainProductInfo;
        if (!mainForm) return true;
        const requiredFields = mainForm.querySelectorAll('[data-custom-property]');
        return requiredFields.length > 0;
      }

      updateQuantityInput() {
        this.currentVariant = this.variantData.find((variant) => variant.id === Number(this.selectedVariantId));
        fetch(`${this.getAttribute('data-url')}?variant=${this.currentVariant.id}&section_id=${this.sectionId}`)
          .then((response) => response.text())
          .then((responseText) => {
            const parsedHTML = new DOMParser().parseFromString(responseText, 'text/html');
            const quantity = document.querySelector(`#QuantitySticky-${this.sectionId}`);
            const newQuantity = parsedHTML.querySelector(`#QuantitySticky-${this.sectionId}`);
            if (newQuantity) {
              quantity.innerHTML = newQuantity.innerHTML;
            }
          })
          .catch((error) => {
            console.error(error);
          })
          .finally(() => {});
      }

      getVariantData() {
        this.variantData =
          this.variantData || JSON.parse(this.container.querySelector('[type="application/json"]').textContent);
        return this.variantData;
      }

      init() {
        if (!this.productFormActions) {
          this.container.classList.add('sticky-atc-bar--show');
          return;
        }

        const mql = window.matchMedia(FoxTheme.config.mediaQueryMobile);
        mql.onchange = this.checkDevice.bind(this);
        this.checkDevice();

        const rootMargin = `-80px 0px 0px 0px`;
        this.observer = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              const method = entry.intersectionRatio !== 1 ? 'add' : 'remove';
              this.container.classList[method]('sticky-atc-bar--show');
            });
          },
          { threshold: 1, rootMargin }
        );
        this.setObserveTarget();
        this.syncWithMainProductForm();
      }

      setObserveTarget() {
        this.observer.observe(this.productFormActions);
        this.observeTarget = this.productFormActions;
      }

      checkDevice(e) {
        document.documentElement.style.setProperty('--sticky-atc-bar-height', this.clientHeight + 'px');
      }

      updateButton(disable = true, text, modifyClass = true) {
        const productForm = this.querySelector('#product-form-sticky-atc-bar');
        if (!productForm) return;

        const addButton = productForm.querySelector('[name="add"]');
        if (!addButton) return;

        const addButtonText = addButton.querySelector('span');
        if (disable) {
          addButton.setAttribute('disabled', 'disabled');
          if (text) addButtonText.textContent = text;
        } else {
          addButton.removeAttribute('disabled');
          addButtonText.textContent = FoxTheme.variantStrings.addToCart;
        }
      }

      updatePrice() {
        const classes = {
          onSale: 'f-price--on-sale',
          soldOut: 'f-price--sold-out',
        };
        const selectors = {
          priceWrapper: '.f-price',
          salePrice: '.f-price-item--sale',
          compareAtPrice: ['.f-price-item--regular'],
          unitPriceWrapper: '.f-price__unit-wrapper',
        };
        const moneyFormat = FoxTheme.settings.moneyFormat;
        const { priceWrapper, salePrice, unitPriceWrapper, compareAtPrice } = FoxTheme.utils.queryDomNodes(
          selectors,
          this
        );
        const unitPrice = unitPriceWrapper.querySelector('.f-price__unit');

        const { compare_at_price, price, unit_price_measurement } = this.currentVariant;

        // On sale.
        if (compare_at_price && compare_at_price > price) {
          priceWrapper.classList.add(classes.onSale);
        } else {
          priceWrapper.classList.remove(classes.onSale);
        }

        // Sold out.
        if (!this.currentVariant.available) {
          priceWrapper.classList.add(classes.soldOut);
        } else {
          priceWrapper.classList.remove(classes.soldOut);
        }

        if (salePrice) salePrice.innerHTML = FoxTheme.Currency.formatMoney(price, moneyFormat);

        if (compareAtPrice && compareAtPrice.length && compare_at_price > price) {
          compareAtPrice.forEach(
            (item) => (item.innerHTML = FoxTheme.Currency.formatMoney(compare_at_price, moneyFormat))
          );
        } else {
          compareAtPrice.forEach((item) => (item.innerHTML = FoxTheme.Currency.formatMoney(price, moneyFormat)));
        }

        if (unit_price_measurement && unitPrice) {
          unitPriceWrapper.classList.remove('hidden');
          const unitPriceContent = `<span>${FoxTheme.Currency.formatMoney(
            this.currentVariant.unit_price,
            moneyFormat
          )}</span>/<span data-unit-price-base-unit>${FoxTheme.Currency.getBaseUnit(this.currentVariant)}</span>`;
          unitPrice.innerHTML = unitPriceContent;
        } else {
          unitPriceWrapper.classList.add('hidden');
        }
      }

      syncWithMainProductForm() {
        FoxTheme.pubsub.subscribe(FoxTheme.pubsub.PUB_SUB_EVENTS.variantChange, (event) => {
          const isMainProduct = event.data.sectionId === this.mainProductInfo.dataset.section;
          if (!isMainProduct) return;
          const variant = event.data.variant;
          const variantInput = this.querySelector('[name="id"]');

          this.currentVariant = variant;
          variantInput.value = variant.id;
          this.updatePrice();
          this.updateButton(true, '', false);
          if (!variant) {
            this.updateButton(true, '', true);
          } else {
            this.updateButton(!variant.available, FoxTheme.variantStrings.soldOut);
          }

          this.updateQuantityInput();
        });
      }
    }
  );
}
